import { useCallback, useEffect, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useAppDispatch, useAppSelector } from 'universal-scripts'
import { useDashboard } from '@hooks/useDashboard'
import {
  initNewDashboard,
  loadDashboard,
  updateDraftTitle,
  togglePublic,
  updateLayout,
  resizeItem,
  removeItem,
  openItemEditor
} from 'src/store/slices/dashboard'
import type { AllowedColSpan, DashboardItem } from 'src/types/dashboard'
import Toggle from '@components/Toggle/Toggle'
import GridLayout from '@components/GridLayout/GridLayout'
import { focusAtEnd } from '@utils/domUtils'

export function BuilderAreaNew() {
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(initNewDashboard())
  }, [dispatch])

  return <BuilderAreaUI />
}

export function BuilderAreaExisting({ id }: { id: string }) {
  const dispatch = useAppDispatch()
  const dashboard = useDashboard(id)
  const isLoaded = useAppSelector(
    (state) => state.dashboard.original?.id === id
  )

  useEffect(() => {
    if (dashboard && !isLoaded) {
      dispatch(loadDashboard(dashboard))
    }
  }, [dashboard, isLoaded, dispatch])

  return <BuilderAreaUI />
}

function BuilderAreaUI() {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const draft = useAppSelector((state) => state.dashboard.draft)
  const textRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    focusAtEnd(textRef.current)
  }, [])

  const handleReorder = useCallback(
    (items: DashboardItem[]) => dispatch(updateLayout(items)),
    [dispatch]
  )

  const handleResize = useCallback(
    (itemId: string, width: AllowedColSpan) =>
      dispatch(resizeItem({ itemId, width })),
    [dispatch]
  )

  const handleRemove = useCallback(
    (itemId: string) => dispatch(removeItem(itemId)),
    [dispatch]
  )

  const handleEdit = useCallback(
    (itemId: string) => {
      const item = draft.items?.find((i) => i.itemId === itemId)
      if (!item) return
      if (item.type === 'label') {
        dispatch(openItemEditor({ type: 'label', itemId }))
      } else if (item.type === 'image') {
        dispatch(openItemEditor({ type: 'image', itemId }))
      } else if (item.type === 'spacer') {
        return
      } else {
        dispatch(openItemEditor({ type: 'chart', itemId, queryId: null }))
      }
    },
    [dispatch, draft.items]
  )

  const handleEditTitle = () => {
    focusAtEnd(textRef.current)
  }

  const handlePublic = () => {
    if (!draft.public) {
      const confirmed = window.confirm(
        intl.formatMessage({ id: 'confirm.public' })
      )
      if (!confirmed) return
    }

    dispatch(togglePublic())
  }

  return (
    <>
      <header id="dashboard-header">
        <div className="side left-side">
          <h1 className="dashboard-builder-title-container">
            <span
              ref={textRef}
              contentEditable
              suppressContentEditableWarning
              role="textbox"
              spellCheck="false"
              className="dashboard-builder-title-editable"
              onBlur={(e) => {
                dispatch(updateDraftTitle(e.currentTarget.textContent))
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  e.currentTarget.blur()
                }
              }}
            >
              {draft.title || <FormattedMessage id="dashboard.new" />}
            </span>

            <button
              type="button"
              className="pen-button"
              onClick={handleEditTitle}
              aria-label="Editar título"
            >
              <span className="icon icon-pen" />
            </button>
          </h1>
        </div>
        <div className="side right-side">
          <Toggle
            checked={!!draft.public}
            onChange={handlePublic}
            leftLabel={intl.formatMessage({ id: 'privacy.private' })}
            rightLabel={intl.formatMessage({ id: 'privacy.public' })}
            id="dashboard-privacy-toggle"
          />
        </div>
      </header>
      <div className="dashboard-builder-area">
        <GridLayout
          items={draft.items ?? []}
          onReorder={handleReorder}
          onResize={handleResize}
          onRemove={handleRemove}
          onEdit={handleEdit}
        />
      </div>
    </>
  )
}
